import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ArrowRight, Medal } from "lucide-react";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Programs", to: "/#programs" },
  { label: "Art", to: "/art" },
  { label: "Books", to: "/books" },
  { label: "Leadership", to: "/leadership-training" },
  { label: "Tutoring", to: "/math-tutoring" },
  { label: "Apparel", to: "/apparel" },
  { label: "Manifesto", to: "/#manifesto" },
  { label: "Author", to: "/#author" },
  { label: "Enroll", to: "/#enroll" },
];

const MENU_ITEMS = [
  { label: "Home", to: "/", desc: "Start here", image: "/menu-home.jpg",
    long: "The front door of Delta DL Enterprise, LLC — courses, books, art, and apparel in one place. Start here to see everything Dr. Louden offers." },
  { label: "Programs", to: "/#programs", desc: "Courses & classes", image: "/menu-programs.jpg",
    long: "Nursing Algebra and Leadership courses, taught online step by step. Built to take you from confusion to confidence." },
  { label: "Leadership", to: "/leadership-training", desc: "Professional training", image: "/menu-leadership.jpg",
    long: "A 4-week online Leadership Training course for professionals, educators, students, and emerging leaders. Build communication, decision-making, and team-building skills. $149." },
  { label: "Tutoring", to: "/math-tutoring", desc: "1-on-1 math help", image: "/menu-tutoring.jpg",
    long: "Personal one-on-one math tutoring with Dr. Louden — patient, plain-language help that finally makes it click. Take a free placement assessment first: Grades 1–8, Grades 9–12, or General Algebra. $50 a session, or five for $225." },
  { label: "Art", to: "/art", desc: "Meaningful art", image: "/menu-art.jpg",
    long: "Transform your home or office with expressive wall art inspired by humanity, nature, and imagination. Explore artwork created by Dennis Louden alongside AI-generated designs developed through his creative direction and selection. Custom original pieces are available for clients." },
  { label: "Books", to: "/books", desc: "The library collection", image: "/menu-books.jpg",
    long: "Nine published titles on mindset, family, health, business, and algebra. Tap any cover to read its story and request your copy." },
  { label: "Apparel", to: "/apparel", desc: "Workout clothing", image: "/menu-apparel.jpg",
    long: "Regular workout clothing of various types — tees, hoodies, leggings, tanks, and cycling gear. Delta DL apparel made to move with you." },
  { label: "Manifesto", to: "/#manifesto", desc: "What we stand for", image: "/menu-manifesto.jpg",
    long: "The creed behind the brand — faith, discipline, and lifting others as we climb. Read what Delta DL Enterprise stands for." },
  { label: "Author", to: "/#author", desc: "Meet Dr. Louden", image: "/menu-author.jpg",
    long: "Meet Dr. Dennis D. Louden — educator, veteran, and author. The story behind every book, canvas, and course on this site." },
  { label: "Enroll", to: "/#enroll", desc: "Begin your application", image: "/menu-enroll.jpg",
    long: "Begin your application in under a minute. Choose your course, share your goals, and Dr. Louden replies to you personally." },
];
const MENU_IMAGE_BY_LABEL = Object.fromEntries(MENU_ITEMS.map((item) => [item.label, item.image]));


export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const location = useLocation();
  const solid = scrolled || location.pathname !== "/";
  const badgeRef = useRef(null);
  const [badgeLeft, setBadgeLeft] = useState(null);

  useEffect(() => {
    const place = () => {
      const badge = badgeRef.current;
      const menu = document.querySelector('[data-testid="nav-menu-tab"]');
      const apply = document.querySelector('[data-testid="nav-enroll-btn"]');
      if (!badge || !menu || !apply) return;
      const container = badge.parentElement.getBoundingClientRect();
      const m = menu.getBoundingClientRect();
      const a = apply.getBoundingClientRect();
      setBadgeLeft((m.right + a.left) / 2 - container.left);
    };
    place();
    const t = setTimeout(place, 350);
    window.addEventListener("resize", place);
    return () => { window.removeEventListener("resize", place); clearTimeout(t); };
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isActive = (to) => location.pathname === to;

  const handleClick = (to) => (e) => {
    const hashIndex = to.indexOf("#");
    if (hashIndex === -1) return;
    const path = to.slice(0, hashIndex) || "/";
    const targetId = to.slice(hashIndex + 1);
    if (location.pathname === path) {
      e.preventDefault();
      const el = document.getElementById(targetId);
      if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
      else if (el) el.scrollIntoView({ behavior: "smooth" });
      setOpen(false);
    }
  };

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        solid ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="relative mx-auto max-w-[1600px] px-6 md:px-12 h-20 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <Link to="/" data-testid="nav-logo" className="flex items-center group">
            <img
              src="/logo.png"
              alt="Delta DL Enterprise, LLC"
              className="h-14 md:h-16 w-auto rounded-lg bg-white px-3 py-1.5"
            />
          </Link>
          <button
            data-testid="nav-menu-tab"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className={`hidden md:flex items-center gap-2 text-sm uppercase tracking-[0.2em] transition-colors ${
              open ? "text-[#7C3AED]" : "text-white/70 hover:text-white"
            }`}
          >
            Menu
            <ChevronDown
              size={16}
              className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
            />
          </button>
        </div>

        <span
          ref={badgeRef}
          data-testid="nav-veteran-badge"
          style={badgeLeft != null ? { left: badgeLeft } : { left: "50%" }}
          className="hidden md:inline-flex absolute -translate-x-1/2 items-center gap-2 rounded-full bg-[#C9A227] px-3 py-2 text-[10px] xl:px-5 xl:py-2.5 xl:text-xs font-bold uppercase tracking-[0.15em] xl:tracking-[0.2em] text-black shadow-[0_0_24px_rgba(201,162,39,0.45)] whitespace-nowrap"
        >
          <Medal size={15} />
          Veteran-Owned Business
        </span>

        <nav className="hidden md:flex items-center gap-6">
          <div className="hidden lg:flex items-center gap-5 text-[11px] uppercase tracking-[0.2em]">
            <Link
              to="/art"
              onClick={handleClick("/art")}
              data-testid="nav-art-link"
              className="text-white/60 transition-colors hover:text-[#A78BFA]"
            >
              Art
            </Link>
            <Link
              to="/books"
              onClick={handleClick("/books")}
              data-testid="nav-books-link"
              className="text-white/60 transition-colors hover:text-[#A78BFA]"
            >
              Books
            </Link>
          </div>
          <Link
            to="/#enroll"
            onClick={handleClick("/#enroll")}
            data-testid="nav-enroll-btn"
            className="magnetic-btn rounded-full bg-[#7C3AED] px-6 py-2.5 text-sm font-medium uppercase tracking-wider text-white hover:bg-white hover:text-black"
          >
            Apply Now
          </Link>
          <Link
            to="/author"
            data-testid="nav-founder-avatar"
            aria-label="About the founder"
            className="group flex items-center gap-3"
          >
            <img
              src="/author.png"
              alt="Dr. Dennis D. Louden"
              className="h-16 w-16 md:h-[72px] md:w-[72px] rounded-full border-2 border-[#7C3AED]/70 object-cover object-top transition-colors group-hover:border-white"
            />
            <span className="hidden lg:block text-left leading-tight">
              <span className="block whitespace-nowrap text-[10px] uppercase tracking-[0.25em] text-white/50">Meet the Founder</span>
              <span className="block whitespace-nowrap text-sm font-medium text-white">Dr. Louden</span>
            </span>
          </Link>
        </nav>

        <div className="flex items-center gap-3 md:hidden">
          <Link to="/author" data-testid="nav-founder-avatar-mobile" aria-label="About the founder">
            <img
              src="/author.png"
              alt="Dr. Dennis D. Louden"
              className="h-14 w-14 rounded-full border-2 border-[#7C3AED]/70 object-cover object-top"
            />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="text-sm uppercase tracking-widest"
          >
            {open ? "Close" : "Menu"}
          </button>
        </div>
      </div>

      {/* Veteran-Owned strip — phones */}
      <div
        data-testid="nav-veteran-badge-mobile"
        className="flex md:hidden items-center justify-center gap-2 bg-[#C9A227] py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-black"
      >
        <Medal size={12} />
        Veteran-Owned Business
      </div>

      {/* Always-visible menu strip for clients on phones */}
      <nav
        data-testid="nav-mobile-strip"
        className="flex md:hidden items-center gap-4 overflow-x-auto px-4 py-2.5 border-t border-white/10 bg-black/60 backdrop-blur-xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {LINKS.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            onClick={handleClick(l.to)}
            data-testid={`nav-strip-${l.label.toLowerCase()}`}
            className={`whitespace-nowrap text-[10px] uppercase tracking-[0.16em] transition-colors ${
              isActive(l.to) ? "text-[#7C3AED]" : "text-white/70"
            }`}
          >
            {l.label}
          </Link>
        ))}
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="flex flex-col p-6 gap-5">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={handleClick(l.to)}
                  data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                  className={`flex items-center gap-4 text-lg uppercase tracking-widest ${
                    isActive(l.to) ? "text-[#7C3AED]" : "text-white/80"
                  }`}
                >
                  <img
                    src={MENU_IMAGE_BY_LABEL[l.label]}
                    alt=""
                    data-testid={`nav-mobile-thumb-${l.label.toLowerCase()}`}
                    className="h-10 w-10 rounded-lg border border-white/10 object-cover"
                  />
                  <span>{l.label}</span>
                </Link>
              ))}
              <Link
                to="/#enroll"
                onClick={handleClick("/#enroll")}
                data-testid="nav-mobile-enroll-btn"
                className="mt-2 rounded-full bg-[#7C3AED] px-6 py-3 text-center text-sm font-medium uppercase tracking-wider text-white"
              >
                Apply Now
              </Link>
            </div>
          </motion.div>
        )}
        {open && (
          <motion.div
            key="desktop-menu"
            data-testid="nav-menu-dropdown"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="hidden md:block overflow-hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="mx-auto max-w-[1200px] px-8 py-10">
              <div className="flex gap-8">
                <div className="w-full lg:w-[560px] shrink-0">
                  <p className="text-[10px] uppercase tracking-[0.35em] text-[#A78BFA]">
                    Delta DL Enterprise, LLC
                  </p>
                  <p className="mb-6 mt-2 font-display text-2xl text-white">Where would you like to go?</p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {MENU_ITEMS.map((m, i) => (
                      <Link
                        key={m.to}
                        to={m.to}
                        onMouseEnter={() => setActiveTab(i)}
                        onFocus={() => setActiveTab(i)}
                        onClick={(e) => { setOpen(false); handleClick(m.to)(e); }}
                        data-testid={`nav-menu-${m.label.toLowerCase()}`}
                        className={`group flex items-center gap-4 rounded-xl border px-5 py-3.5 transition-all duration-200 ${
                          activeTab === i
                            ? "border-[#7C3AED]/60 bg-[#7C3AED]/15"
                            : "border-white/10 bg-white/[0.03] hover:border-[#7C3AED]/40"
                        }`}
                      >
                        <span className="font-display text-xs tracking-widest text-[#7C3AED]/70">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <img
                          src={m.image}
                          alt=""
                          data-testid={`nav-menu-thumb-${m.label.toLowerCase()}`}
                          className="h-11 w-11 shrink-0 rounded-lg border border-white/10 object-cover"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block font-display text-lg uppercase tracking-wide text-white">
                            {m.label}
                          </span>
                          <span className="block text-[11px] text-white/45 lg:hidden">{m.desc}</span>
                        </span>
                        <ArrowRight
                          size={15}
                          className={`transition-all duration-200 ${
                            activeTab === i ? "translate-x-0.5 text-[#A78BFA]" : "text-white/25"
                          }`}
                        />
                      </Link>
                    ))}
                  </div>
                </div>
                <div
                  data-testid="nav-menu-preview"
                  className="hidden lg:flex flex-1 flex-col justify-center rounded-2xl border border-[#7C3AED]/25 bg-gradient-to-b from-[#7C3AED]/10 to-transparent p-10"
                >
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <span className="text-[10px] uppercase tracking-[0.35em] text-[#A78BFA]">
                      {MENU_ITEMS[activeTab].desc}
                    </span>
                    <h3 className="mt-3 font-display text-4xl uppercase tracking-wide text-white">
                      {MENU_ITEMS[activeTab].label}
                    </h3>
                    <p className="mt-4 max-w-md text-sm leading-relaxed text-white/60">
                      {MENU_ITEMS[activeTab].long}
                    </p>
                    <Link
                      to={MENU_ITEMS[activeTab].to}
                      onClick={(e) => { setOpen(false); handleClick(MENU_ITEMS[activeTab].to)(e); }}
                      data-testid="nav-menu-preview-open"
                      className="magnetic-btn mt-8 inline-flex items-center gap-2 rounded-full bg-[#7C3AED] px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-black"
                    >
                      Open {MENU_ITEMS[activeTab].label}
                      <ArrowRight size={14} />
                    </Link>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Nav;
